/**
 * Route Hooks — every non-list GET (query builder) and every mutation.
 *
 * NO AUTOMATIC REFRESH: a mutation refreshes nothing on its own. The caller's
 * `onSuccess` names the queries its write made stale, with keys from the
 * generated `queryKeys` (see query-keys.ts, use-invalidate-queries.ts), and the
 * screen's realtime provider refreshes what other tabs and users change.
 *
 * Optimistic updates are opt-in via `optimistic: { type: "patch", … }`.
 *
 * Note: These hooks automatically unwrap the `data` key from API responses,
 * so you access `result.data` directly instead of `result.data.data`.
 */

import { stringifyQuery } from "@birdwatch/common";
import type {
  InfiniteData,
  QueryKey,
  UseInfiniteQueryOptions,
  UseInfiniteQueryResult,
  UseMutationResult,
  UseQueryOptions,
  UseQueryResult,
} from "@tanstack/react-query";
import {
  keepPreviousData,
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { useSessionToken } from "./use-session-token";
import { getClientConfig } from "../types";
import { nextPageOffset } from "./next-page-offset";
import { customQueryKey } from "./query-keys";
import type { QueryBuilder } from "./types";
import { buildRequestHeaders } from "./request-headers";

// ============================================================================
// Types
// ============================================================================

export interface CustomQueryOptions {
  enabled?: boolean;
  staleTime?: number;
  gcTime?: number;
  refetchOnWindowFocus?: boolean;
  refetchOnMount?: boolean;
  /**
   * Keep the previous page rendered while the key changes — pass
   * `keepPreviousData` from `@tanstack/react-query`. Search-as-you-type
   * endpoints need this to avoid a skeleton flash on every keystroke.
   */
  placeholderData?: UseQueryOptions<
    CustomResult<unknown>,
    CustomApiError
  >["placeholderData"];
  /**
   * Poll cadence, or a function of the query deciding it per tick — the
   * TanStack `refetchInterval` contract. Used by projections a backend job
   * fills in asynchronously (poll while `pending`, stop once it lands).
   */
  refetchInterval?: UseQueryOptions<
    CustomResult<unknown>,
    CustomApiError
  >["refetchInterval"];
  /**
   * Whether and how often a failed request is retried — the TanStack `retry`
   * contract. Omitted, the app-level QueryClient default applies. A page whose
   * 4xx answer is final (an unknown link) passes a function that stops on it,
   * so the not-found state shows at once instead of after every retry.
   */
  retry?: UseQueryOptions<CustomResult<unknown>, CustomApiError>["retry"];
  /**
   * Client-side request deadline in ms (Rule 21). Omitted, the request runs
   * without one — the browser/network default applies.
   */
  timeoutMs?: number;
}

/**
 * Options for `.getInfinite()` on custom query builders.
 * Mirrors the shape used by `ListQueryBuilder.getInfinite()`.
 */
export type CustomInfiniteQueryOptions<TResponse> = Omit<
  UseInfiniteQueryOptions<
    CustomResult<TResponse>,
    CustomApiError,
    InfiniteData<CustomResult<TResponse>>
  >,
  "queryKey" | "queryFn" | "getNextPageParam" | "initialPageParam"
>;

/**
 * Variables accepted by `mutate()` for a custom mutation.
 *
 * - Route without path params (`TParams` left at its `never` default): the
 *   request body itself.
 * - Route with path params: a `{ params, body }` envelope — path params are
 *   supplied per call, so one hook instance can target different records.
 *   `body` is optional when the endpoint takes no body (`TBody` is `unknown`)
 *   or an optional one (`undefined extends TBody`). `headers` adds request
 *   headers to this call only, over the hook's `headers` option.
 */
export type CustomMutationVariables<TBody, TParams> = [TParams] extends [never]
  ? TBody
  : unknown extends TBody
    ? { params: TParams; body?: TBody; headers?: Record<string, string> }
    : undefined extends TBody
      ? { params: TParams; body?: TBody; headers?: Record<string, string> }
      : { params: TParams; body: TBody; headers?: Record<string, string> };

/**
 * Extra request headers for every call of a mutation hook: a map, or a function
 * read when each mutation starts, so it sees the caller's latest state. The
 * package's own headers always win (request-headers.ts).
 */
export type CustomMutationHeaders =
  | Record<string, string>
  | (() => Record<string, string> | undefined);

/**
 * Opt-in optimistic update: a pure patch applied to every cached query matching
 * `queryKey` before the request. Snapshotted first and restored on error.
 */
export type CustomMutationOptimistic<TVars = unknown> = {
  type: "patch";
  queryKey: QueryKey;
  apply: (data: any, vars: TVars) => any;
};

/** Mutation options for custom endpoints (unwrapped data) */
export type CustomMutationOptions<
  TInput,
  TOutput,
  TParams extends Record<string, string> = never,
> = Omit<
  import("@tanstack/react-query").UseMutationOptions<
    TOutput,
    CustomApiError,
    CustomMutationVariables<TInput, TParams>
  >,
  "mutationFn"
> & {
  /** Apply an optimistic patch before the network roundtrip (restored on error). */
  optimistic?: CustomMutationOptimistic<
    CustomMutationVariables<TInput, TParams>
  >;
  /**
   * Client-side request deadline in ms (Rule 21). Omitted, the request runs
   * without one — correct for genuinely long calls (e.g. AI generation).
   */
  timeoutMs?: number;
  /** Extra request headers for every call; a call's envelope `headers` win over these. */
  headers?: CustomMutationHeaders;
};

/** Response wrapper type */
export interface CustomResult<T> {
  data: T;
  extra?: {
    hasNextPage?: boolean;
    total?: number | null;
    aggregates?: Record<string, number>;
  };
}

/** API error response structure */
export interface ApiErrorResponse {
  error: {
    code: string;
    message: string;
    errors?: Record<string, string[]>;
    retryAfter?: number;
  };
}

/** Custom API error with structured error info */
export class CustomApiError extends Error {
  code: string;
  errors?: Record<string, string[]>;
  retryAfter?: number;
  status?: number;

  constructor(response: ApiErrorResponse, status?: number) {
    super(response.error.message);
    this.name = "CustomApiError";
    this.code = response.error.code;
    this.errors = response.error.errors;
    this.retryAfter = response.error.retryAfter;
    this.status = status;
  }
}

/** Unwrapped query result with typed extra */
export interface UnwrappedCustomQueryResult<T, TExtra = undefined>
  extends Omit<UseQueryResult<CustomResult<T>, CustomApiError>, "data"> {
  data: T | undefined;
  extra: TExtra;
}

/**
 * Shape a non-2xx response into the client's error. The platform's
 * `{ error: { code, message } }` envelope is preserved; anything else gets a
 * generic code with the best message available. Shared by every transport in
 * this package — JSON queries, mutations, and the SSE stream hook.
 */
export async function readApiError(
  response: Response,
): Promise<CustomApiError> {
  const errorBody = await response.json().catch(() => null);

  if (errorBody?.error?.message) {
    return new CustomApiError(errorBody as ApiErrorResponse, response.status);
  }

  return new CustomApiError(
    {
      error: {
        code: "REQUEST_FAILED",
        message:
          errorBody?.message ||
          response.statusText ||
          `Request failed: ${response.status}`,
      },
    },
    response.status,
  );
}

// ============================================================================
// Query Helper
// ============================================================================

async function fetchCustom<T>(
  url: string,
  token: string | null,
  timeoutMs?: number,
): Promise<CustomResult<T>> {
  const response = await fetch(url, {
    headers: buildRequestHeaders(token),
    ...(timeoutMs ? { signal: AbortSignal.timeout(timeoutMs) } : {}),
  });

  if (!response.ok) throw await readApiError(response);

  return response.json();
}

// ============================================================================
// Mutation Helper
// ============================================================================

async function mutateCustom<T>(
  url: string,
  method: string,
  headers: Record<string, string>,
  body?: unknown,
  timeoutMs?: number,
): Promise<CustomResult<T>> {
  const response = await fetch(url, {
    method,
    headers,
    ...(body ? { body: JSON.stringify(body) } : {}),
    ...(timeoutMs ? { signal: AbortSignal.timeout(timeoutMs) } : {}),
  });

  if (!response.ok) throw await readApiError(response);

  // Handle 204 No Content
  if (response.status === 204) {
    return { data: undefined as T };
  }

  return response.json();
}

// ============================================================================
// Query String Builder
// ============================================================================

function buildQueryString(query: Record<string, unknown>): string {
  return stringifyQuery(query, { addPrefix: true });
}

// ============================================================================
// Path Template Resolution
// ============================================================================

/**
 * Whether every `:param` of a path template has a non-empty value. A query
 * whose id is not known yet (`useGetOrdersByOrderId({ orderId: "" })` while a
 * route param loads) must not fire: the request would hit the parent path.
 */
export function hasEveryPathParam(
  path: string,
  params: Record<string, string> | undefined,
): boolean {
  return (path.match(/:(\w+)/g) ?? []).every((token) =>
    Boolean(params?.[token.slice(1)]),
  );
}

/**
 * Replace `:param` tokens in a path template with values from `params`.
 * Tokens without a matching key are left as-is.
 * Exported for testing — production callers go through the hooks/builders.
 */
export function resolvePathParams(
  path: string,
  params: Record<string, string> | undefined,
): string {
  if (!params) return path;
  return path.replace(/:(\w+)/g, (match, paramName) =>
    paramName in params ? String(params[paramName]) : match,
  );
}

/**
 * Split mutate() variables into path params, request body and per-call headers.
 *
 * The discriminator is the path template — it mirrors the type-level rule of
 * `CustomMutationVariables`: a path with `:param` tokens receives the
 * `{ params, body, headers }` envelope, a paramless path receives the body
 * itself (and so carries no per-call headers).
 * Exported for testing — production callers use `useResourceCustomMutation`.
 */
export function splitMutationVariables<TBody>(
  path: string,
  variables: unknown,
): {
  params: Record<string, string> | undefined;
  body: TBody;
  headers: Record<string, string> | undefined;
} {
  if (!/:\w+/.test(path)) {
    return { params: undefined, body: variables as TBody, headers: undefined };
  }
  const envelope = variables as {
    params: Record<string, string>;
    body?: TBody;
    headers?: Record<string, string>;
  };
  return {
    params: envelope.params,
    body: envelope.body as TBody,
    headers: envelope.headers,
  };
}

/**
 * A mutation's extra request headers: the hook's `headers` option, read now so
 * a function sees the caller's latest state, overlaid by the call's own
 * envelope `headers`. Exported for testing — production callers use
 * `useResourceCustomMutation`.
 */
export function resolveMutationHeaders(
  hookHeaders: CustomMutationHeaders | undefined,
  callHeaders: Record<string, string> | undefined,
): Record<string, string> {
  const fromHook =
    typeof hookHeaders === "function" ? hookHeaders() : hookHeaders;
  return { ...fromHook, ...callHeaders };
}

// ============================================================================
// CustomQueryBuilder Class
// ============================================================================

/**
 * Custom Query Builder for non-CRUD GET endpoints.
 *
 * Implements QueryBuilder interface for prefetch and useMultipleQueries support.
 * Uses a fluent builder pattern consistent with ListQueryBuilder.
 *
 * @template TResponse - The response type
 * @template TParams - Path parameters type
 * @template TQuery - Query parameters type
 * @template TExtra - The extra metadata type (optional, defaults to DefaultExtra)
 *
 * @example
 * ```tsx
 * // Basic usage
 * const { data } = useOrdersVisits({ orderId: "123" }).get();
 *
 * // With query parameters
 * const { data } = useOrdersVisits({ orderId: "123" })
 *   .query({ take: 10, skip: 0 })
 *   .get();
 *
 * // With options
 * const { data } = useOrdersVisits({ orderId: "123" })
 *   .query({ take: 10 })
 *   .get({ staleTime: 5000 });
 *
 * // Prefetching
 * const prefetch = usePrefetch();
 * prefetch(useOrdersVisits({ orderId: "123" }).query({ take: 10 }));
 * ```
 */
export class CustomQueryBuilder<
  TResponse,
  TParams extends Record<string, string> = Record<string, string>,
  TQuery extends Record<string, unknown> = Record<string, unknown>,
  TExtra = undefined,
> implements QueryBuilder<CustomResult<TResponse>>
{
  private _resource: string;
  private _path: string;
  private _params: TParams | undefined;
  private _query: TQuery | undefined;

  constructor(
    resource: string,
    path: string,
    params?: TParams,
    query?: TQuery,
  ) {
    this._resource = resource;
    this._path = path;
    this._params = params;
    this._query = query;
  }

  // ==========================================================================
  // Fluent Methods
  // ==========================================================================

  /**
   * Set query parameters.
   *
   * @example
   * ```tsx
   * useOrdersVisits({ orderId }).query({ take: 10, skip: 0 }).get()
   * ```
   */
  query(query: TQuery): CustomQueryBuilder<TResponse, TParams, TQuery, TExtra> {
    return new CustomQueryBuilder(
      this._resource,
      this._path,
      this._params,
      query,
    );
  }

  // ==========================================================================
  // QueryBuilder Interface Implementation
  // ==========================================================================

  /**
   * Get the query key for this query (useful for invalidation).
   *
   * Trailing slots are omitted when undefined so the key stays compact:
   * `[resource, "custom", path]` → `[..., params]` → `[..., params, query]`.
   * If query is set but params is not, an explicit `undefined` placeholder
   * keeps query in slot 4 to preserve positional meaning.
   */
  getQueryKey(): readonly unknown[] {
    return customQueryKey(
      this._resource,
      this._path,
      this._params,
      this._query,
    );
  }

  /**
   * Build the full URL for the request.
   *
   * `extraSkip` is used by `.getInfinite()` to inject the per-page offset.
   * It overrides any `skip` set via `.query({ skip })` for that single page.
   */
  toURL(extraSkip?: number): string {
    const config = getClientConfig();

    const resolvedPath = resolvePathParams(this._path, this._params);

    // Merge per-page skip into the query if provided
    const effectiveQuery =
      extraSkip !== undefined
        ? { ...(this._query ?? {}), skip: extraSkip }
        : this._query;

    // Build query string
    const queryString = effectiveQuery ? buildQueryString(effectiveQuery) : "";

    // Normalize "/" path to "" to avoid trailing slash before query string
    const normalizedPath = resolvedPath === "/" ? "" : resolvedPath;
    return `${config.baseUrl}/${this._resource}${normalizedPath}${queryString}`;
  }

  /**
   * Fetch data from the server.
   * Used by usePrefetch and internally by useQuery.
   *
   * @param token - Authentication token
   * @param extraSkip - Per-page offset for infinite pagination
   * @returns Promise resolving to custom result
   */
  fetch(
    token: string | null,
    extraSkip?: number,
  ): Promise<CustomResult<TResponse>> {
    return fetchCustom<TResponse>(this.toURL(extraSkip), token);
  }

  // ==========================================================================
  // State Access
  // ==========================================================================

  getResource(): string {
    return this._resource;
  }

  getParams(): TParams | undefined {
    return this._params;
  }

  getQuery(): TQuery | undefined {
    return this._query;
  }

  // ==========================================================================
  // Terminal Method - TanStack Query Integration
  // ==========================================================================

  /**
   * Execute the query using TanStack Query.
   * Returns unwrapped data directly.
   *
   * @param options - TanStack Query options
   * @returns Query result with data unwrapped (data is TResponse, not { data: TResponse })
   *
   * @example
   * ```tsx
   * const { data, isLoading } = useOrdersVisits({ orderId })
   *   .query({ take: 10 })
   *   .get();
   * ```
   */
  get(
    options?: CustomQueryOptions,
  ): UnwrappedCustomQueryResult<TResponse, TExtra> {
    const getToken = useSessionToken();
    const url = this.toURL();
    const queryKey = this.getQueryKey();

    const queryFn = async (): Promise<CustomResult<TResponse>> => {
      const token = await getToken();
      return fetchCustom<TResponse>(url, token, options?.timeoutMs);
    };

    // Spread only the keys the caller actually set: an explicit
    // `staleTime: undefined` would override the app-level QueryClient
    // defaults rather than fall back to them.
    const result = useQuery<CustomResult<TResponse>, CustomApiError>({
      queryKey,
      queryFn,
      enabled:
        hasEveryPathParam(this._path, this._params) &&
        (options?.enabled ?? true),
      ...(options?.staleTime !== undefined && { staleTime: options.staleTime }),
      ...(options?.gcTime !== undefined && { gcTime: options.gcTime }),
      ...(options?.refetchOnWindowFocus !== undefined && {
        refetchOnWindowFocus: options.refetchOnWindowFocus,
      }),
      ...(options?.refetchOnMount !== undefined && {
        refetchOnMount: options.refetchOnMount,
      }),
      ...(options?.placeholderData !== undefined && {
        placeholderData: options.placeholderData as never,
      }),
      ...(options?.refetchInterval !== undefined && {
        refetchInterval: options.refetchInterval as never,
      }),
      ...(options?.retry !== undefined && { retry: options.retry }),
    });

    // Unwrap data and extra
    return {
      ...result,
      data: result.data?.data,
      extra: result.data?.extra as TExtra,
    } as UnwrappedCustomQueryResult<TResponse, TExtra>;
  }

  /**
   * Execute the query with infinite scroll support.
   *
   * Requires the custom endpoint to:
   * - Return an array as `data` (so pages can be flattened)
   * - Include `extra.hasNextPage` in the response envelope
   * - Accept `take`/`skip` query params for offset pagination
   *
   * Page size comes from `.query({ take })` (defaults to 20). The `skip`
   * offset is injected per page from the cumulative number of items loaded.
   *
   * Defaults `placeholderData: keepPreviousData` so existing pages stay
   * rendered while filters/sort change — preserves scroll position and
   * avoids skeleton flicker. Pass `placeholderData: undefined` to opt out.
   *
   * @example
   * ```tsx
   * const { data, fetchNextPage, hasNextPage, isFetchingNextPage } =
   *   useGetBillingPaymentHistoriesByAccountId({ accountId })
   *     .query({ take: 10 })
   *     .getInfinite();
   *
   * const items = data?.pages.flatMap((p) => p.data) ?? [];
   * ```
   */
  getInfinite(
    options?: CustomInfiniteQueryOptions<TResponse>,
  ): UseInfiniteQueryResult<
    InfiniteData<CustomResult<TResponse>>,
    CustomApiError
  > {
    const getToken = useSessionToken();
    const queryKey = [...this.getQueryKey(), "infinite"] as const;

    return useInfiniteQuery<
      CustomResult<TResponse>,
      CustomApiError,
      InfiniteData<CustomResult<TResponse>>
    >({
      queryKey,
      queryFn: async ({ pageParam }) => {
        const token = await getToken();
        const url = this.toURL(pageParam as number);
        return fetchCustom<TResponse>(url, token);
      },
      initialPageParam: 0 as number,
      getNextPageParam: nextPageOffset,
      placeholderData: keepPreviousData,
      ...options,
      enabled:
        hasEveryPathParam(this._path, this._params) &&
        (options?.enabled ?? true),
    });
  }
}

// ============================================================================
// Factory Function
// ============================================================================

/**
 * Create a custom query builder.
 *
 * Returns a builder that can be used to fetch data from custom endpoints
 * with TanStack Query integration, prefetching, and useMultipleQueries support.
 *
 * @param resource - Resource name
 * @param path - API path with :params
 * @param params - Path parameters
 * @returns CustomQueryBuilder instance
 *
 * @example
 * ```tsx
 * // Generated hook usage
 * const { data } = useOrdersVisits({ orderId: "123" })
 *   .query({ take: 10 })
 *   .get();
 * ```
 */
export function useResourceCustomQuery<
  TResponse,
  TParams extends Record<string, string> = Record<string, string>,
  TQuery extends Record<string, unknown> = Record<string, unknown>,
  TExtra = undefined,
>(
  resource: string,
  path: string,
  params?: TParams,
): CustomQueryBuilder<TResponse, TParams, TQuery, TExtra> {
  return new CustomQueryBuilder(resource, path, params);
}

// ============================================================================
// useResourceCustomMutation Hook
// ============================================================================

/**
 * Mutation hook for every non-GET endpoint.
 *
 * Refreshes nothing on its own: pass `onSuccess` and name the queries the
 * write made stale (keys from the generated `queryKeys`). Optimistic updates
 * are opt-in via `optimistic: { type: "patch", … }`.
 *
 * Path params are supplied at mutate() time. For a route with `:param`
 * tokens, mutate() takes a `{ params, body }` envelope (body optional when
 * the endpoint has no body); for a paramless route it takes the body itself.
 *
 * Extra request headers come from the `headers` option (every call) and, for
 * a route with `:param` tokens, the envelope's `headers` (this call, winning
 * over the option). They never replace the package's own headers.
 *
 * @param resource - Resource name
 * @param path - API path with :params
 * @param method - HTTP method (POST, PUT, PATCH, DELETE)
 * @param options - TanStack mutation options, plus `optimistic`, `timeoutMs` and `headers`
 *
 * @example
 * ```tsx
 * const invalidate = useInvalidateQueries();
 * const update = useUpdateOrdersByOrderId({
 *   onSuccess: (_order, { params }) =>
 *     invalidate(queryKeys.getOrdersByOrderId(params), queryKeys.getOrdersList()),
 * });
 * update.mutate({ params: { orderId }, body: { placeId } });
 * ```
 */
export function useResourceCustomMutation<
  TBody,
  TResponse,
  TParams extends Record<string, string> = never,
>(
  resource: string,
  path: string,
  method: "POST" | "PUT" | "PATCH" | "DELETE",
  options?: CustomMutationOptions<TBody, TResponse, TParams>,
): UseMutationResult<
  TResponse,
  CustomApiError,
  CustomMutationVariables<TBody, TParams>
> {
  type TVariables = CustomMutationVariables<TBody, TParams>;

  const getToken = useSessionToken();
  const config = getClientConfig();
  const queryClient = useQueryClient();

  const {
    onMutate: userOnMutate,
    onSuccess: userOnSuccess,
    onError: userOnError,
    optimistic,
    timeoutMs,
    headers,
    ...restOptions
  } = options || {};

  return useMutation({
    mutationFn: async (variables: TVariables): Promise<TResponse> => {
      const {
        params,
        body,
        headers: callHeaders,
      } = splitMutationVariables<TBody>(path, variables);
      const extraHeaders = resolveMutationHeaders(headers, callHeaders);
      const token = await getToken();
      const resolvedPath = resolvePathParams(path, params);
      // Avoid trailing slash when path is "/" (causes CORS issues with Next.js redirects)
      const pathSuffix = resolvedPath === "/" ? "" : resolvedPath;
      const url = `${config.baseUrl}/${resource}${pathSuffix}`;

      // Body is purely the mutation data - no path param extraction. Send body for DELETE when provided (e.g. remove user accounts).
      const response = await mutateCustom<TResponse>(
        url,
        method,
        buildRequestHeaders(token, extraHeaders),
        body,
        timeoutMs,
      );
      return response.data;
    },

    onMutate: async (variables: TVariables) => {
      let rollback: (() => void) | undefined;

      if (optimistic) {
        await queryClient.cancelQueries({ queryKey: optimistic.queryKey });
        const snapshots = queryClient.getQueriesData<unknown>({
          queryKey: optimistic.queryKey,
        });
        queryClient.setQueriesData<unknown>(
          { queryKey: optimistic.queryKey },
          (old: unknown) =>
            old !== undefined ? optimistic.apply(old, variables) : old,
        );
        rollback = () => {
          for (const [key, data] of snapshots) {
            queryClient.setQueryData(key, data);
          }
        };
      }

      // @ts-expect-error - TanStack Query v5 onMutate signature
      const userContext = await userOnMutate?.(variables);
      return { rollback, userContext };
    },

    onSuccess: (data, variables, context) => {
      const userContext = (context as { userContext?: unknown } | undefined)
        ?.userContext;
      // @ts-expect-error - TanStack Query v5 onSuccess has different signature
      return userOnSuccess?.(data, variables, userContext);
    },

    onError: (err, variables, context) => {
      const ctx = context as
        | { rollback?: () => void; userContext?: unknown }
        | undefined;
      ctx?.rollback?.();

      // @ts-expect-error - TanStack Query v5 onError signature
      userOnError?.(err, variables, ctx?.userContext);
    },

    ...restOptions,
  });
}
